import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BadgeCheck,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  CalendarRange,
  ChartColumn,
  ChartLine,
  ClipboardPen,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  FileStack,
  FileText,
  FileUp,
  GraduationCap,
  LayoutDashboard,
  ListChecks,
  MapPinned,
  MonitorSmartphone,
  PartyPopper,
  Plug,
  Scale,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Stamp,
  TriangleAlert,
  UserCog,
  Users,
  Workflow,
} from 'lucide-react';
import { Permissions as P, WORKFLOW_APPROVER_PERMISSIONS, type MeDto, type PermissionCode } from '@fas/types';
import { hasAny } from '@/lib/permissions';
import { ANALYTICS_READERS, PAYROLL_READERS, REVIEW_QUEUE_READERS } from '@/features/payroll/labels';
import { REQUEST_AREA_PERMISSIONS } from '@/features/requests/labels';
import { EVENT_AREA } from '@/features/events/labels';
import { LEAVE_AREA } from '@/features/leave/labels';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown when the user has any of these permissions (empty = everyone). */
  any: PermissionCode[];
  section: 'main' | 'admin';
  /** Only for accounts linked to a faculty profile. */
  facultyOnly?: boolean;
  /** Highlight only on an exact URL match. */
  end?: boolean;
  /** Live count shown next to the label. */
  badge?: 'approvals';
}

export const EXCEPTION_READERS: PermissionCode[] = [
  P.ATTENDANCE_READ_DEPARTMENT,
  P.ATTENDANCE_VERIFY,
  P.ATTENDANCE_APPROVE,
  P.ATTENDANCE_FINALIZE,
  P.DASHBOARD_READ_DEPARTMENT,
  P.ATTENDANCE_CORRECT_APPROVE,
  P.PROOF_VERIFY,
];

export const REPORT_READERS: PermissionCode[] = [
  P.REPORT_READ_DEPARTMENT,
  P.ATTENDANCE_READ_DEPARTMENT,
  P.ATTENDANCE_CORRECT_APPROVE,
  P.ATTENDANCE_VERIFY,
  P.ATTENDANCE_APPROVE,
  P.ATTENDANCE_FINALIZE,
  P.MAKEUP_APPROVE,
  P.SUBSTITUTION_APPROVE,
  P.OVERLOAD_APPROVE,
  P.DUTY_APPROVE,
  P.DUTY_MANAGE,
  P.SCHEDULE_READ_DEPARTMENT,
  P.SCHEDULE_MANAGE,
  P.FACULTY_LOAD_MANAGE,
];

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, any: [], section: 'main', end: true },
  { to: '/my-schedule', label: 'My schedule', icon: CalendarClock, any: [P.SCHEDULE_READ_SELF], section: 'main', facultyOnly: true },
  { to: '/attendance/history', label: 'My attendance', icon: ClipboardCheck, any: [P.ATTENDANCE_READ_SELF], section: 'main', facultyOnly: true },
  { to: '/requests', label: 'Requests', icon: FileStack, any: REQUEST_AREA_PERMISSIONS, section: 'main' },
  { to: '/approvals', label: 'Approvals', icon: Stamp, any: [...WORKFLOW_APPROVER_PERMISSIONS], section: 'main', badge: 'approvals' },
  { to: '/attendance/dtr', label: 'Daily time record', icon: FileText, any: [P.REPORT_READ_SELF, P.REPORT_READ_DEPARTMENT], section: 'main' },
  { to: '/attendance/live', label: 'Live attendance', icon: Activity, any: [P.DASHBOARD_READ_DEPARTMENT], section: 'main' },
  {
    to: '/attendance/sessions',
    label: 'Attendance records',
    icon: ListChecks,
    any: [P.ATTENDANCE_READ_DEPARTMENT, P.ATTENDANCE_VERIFY, P.ATTENDANCE_APPROVE, P.ATTENDANCE_FINALIZE],
    section: 'main',
  },
  { to: '/attendance/review', label: 'Review queue', icon: ClipboardPen, any: REVIEW_QUEUE_READERS, section: 'main' },
  { to: '/attendance/anomalies', label: 'Anomalies', icon: ShieldAlert, any: [P.ATTENDANCE_VERIFY], section: 'main' },
  { to: '/exceptions', label: 'Exceptions', icon: TriangleAlert, any: EXCEPTION_READERS, section: 'main' },
  { to: '/payroll', label: 'Payroll cut-offs', icon: CalendarCheck, any: PAYROLL_READERS, section: 'main' },
  { to: '/faculty', label: 'Faculty', icon: Users, any: [P.FACULTY_READ_DEPARTMENT, P.FACULTY_MANAGE], section: 'main' },
  { to: '/schedules', label: 'Class schedules', icon: CalendarRange, any: [P.SCHEDULE_READ_DEPARTMENT, P.SCHEDULE_MANAGE], section: 'main', end: true },
  { to: '/faculty-loads', label: 'Faculty loads', icon: ClipboardList, any: [P.FACULTY_LOAD_MANAGE, P.SCHEDULE_READ_DEPARTMENT], section: 'main' },
  { to: '/schedules/import', label: 'Schedule import', icon: FileUp, any: [P.SCHEDULE_IMPORT], section: 'main' },
  { to: '/leave', label: 'Leave', icon: CalendarOff, any: LEAVE_AREA, section: 'main' },
  { to: '/events', label: 'Events', icon: PartyPopper, any: EVENT_AREA, section: 'main' },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays, any: [], section: 'main' },
  { to: '/reports', label: 'Reports', icon: ChartLine, any: ANALYTICS_READERS, section: 'main' },
  { to: '/reports/requests', label: 'Request reports', icon: ChartColumn, any: REPORT_READERS, section: 'main' },
  { to: '/admin/users', label: 'Users', icon: UserCog, any: [P.USER_READ, P.USER_MANAGE], section: 'admin' },
  { to: '/admin/roles', label: 'Roles & permissions', icon: ShieldCheck, any: [P.ROLE_READ, P.ROLE_MANAGE], section: 'admin' },
  { to: '/admin/organization', label: 'Organization', icon: Building2, any: [P.ORG_MANAGE, P.LOCATION_MANAGE], section: 'admin' },
  { to: '/admin/academic', label: 'Academic periods', icon: GraduationCap, any: [P.ACADEMIC_MANAGE], section: 'admin' },
  { to: '/admin/employment-types', label: 'Employment types', icon: BadgeCheck, any: [P.EMPLOYMENT_TYPE_MANAGE], section: 'admin' },
  { to: '/admin/attendance-policies', label: 'Attendance policies', icon: SlidersHorizontal, any: [P.POLICY_MANAGE, P.ATTENDANCE_FINALIZE], section: 'admin' },
  { to: '/admin/workflows', label: 'Approval workflows', icon: Workflow, any: [P.WORKFLOW_MANAGE], section: 'admin' },
  { to: '/admin/duty-categories', label: 'Duty categories', icon: Briefcase, any: [P.DUTY_MANAGE], section: 'admin' },
  { to: '/admin/leave-policies', label: 'Leave policies', icon: Scale, any: [P.LEAVE_MANAGE], section: 'admin' },
  { to: '/admin/geofences', label: 'Geofences', icon: MapPinned, any: [P.GEOFENCE_MANAGE, P.LOCATION_MANAGE], section: 'admin' },
  { to: '/admin/devices', label: 'Devices', icon: Smartphone, any: [P.DEVICE_READ, P.DEVICE_MANAGE], section: 'admin' },
  { to: '/admin/displays', label: 'Displays and room tags', icon: MonitorSmartphone, any: [P.DISPLAY_READ, P.DISPLAY_MANAGE], section: 'admin' },
  { to: '/admin/cards', label: 'ID cards', icon: CreditCard, any: [P.CARD_READ, P.CARD_MANAGE], section: 'admin' },
  { to: '/admin/integrations', label: 'Integrations', icon: Plug, any: [P.INTEGRATION_READ, P.INTEGRATION_MANAGE], section: 'admin' },
  { to: '/admin/audit', label: 'Audit log', icon: ScrollText, any: [P.AUDIT_READ], section: 'admin' },
  { to: '/admin/settings', label: 'Settings', icon: Settings, any: [P.SETTINGS_MANAGE], section: 'admin' },
];

export function visibleNavItems(items: NavItem[], me: Pick<MeDto, 'permissions' | 'faculty'> | null | undefined): NavItem[] {
  return items.filter((i) => (i.any.length === 0 || hasAny(me?.permissions, i.any)) && (!i.facultyOnly || Boolean(me?.faculty)));
}
